import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Cookies from 'universal-cookie';
import moment from "moment";

dayjs.extend(utc);
dayjs.extend(timezone);
const cookies = new Cookies();
dayjs.tz.setDefault(cookies.get("timeZone"));

const calculateGranularity = (calendarStartDate, calendarEndDate, previousGranularity=null, defaultHighLevelGranularity="weekly") => {
  const startDate = dayjs(calendarStartDate);
  const endDate = dayjs(calendarEndDate);
  const differenceInDays = endDate.diff(startDate, 'day');
  
  return differenceInDays > 31 ? defaultHighLevelGranularity : (previousGranularity || 'daily')
}

/**
 * Formats a date range for display in chart headers
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted date range string or "Select Date Range" if dates are missing
 */
const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return "Select Date Range";
  }
  const start = moment(startDate).format("D MMM YYYY");
  const end = moment(endDate).format("D MMM YYYY");
  return `${start} - ${end}`;
};

/**
 * Formats date labels for chart axes (handles both single dates and date ranges)
 * @param {string} label - Date label (can be a single date or date range)
 * @returns {string} Formatted date label
 */
const formatDateLabel = (label) => {
  if (!label) return label;

  // Check if it's a date range (contains " - ")
  if (label.includes(" - ")) {
    const [startDate, endDate] = label.split(" - ");
    const formattedStart = moment(startDate.trim()).format("DD MMM");
    const formattedEnd = moment(endDate.trim()).format("DD MMM");
    return `${formattedStart} - ${formattedEnd}`;
  }

  // Single date format
  return moment(label).format("DD MMM");
};

/**
 * Formats a date for API calls (YYYY-MM-DD format)
 * @param {string|Date} date - Date to format
 * @returns {string|null} Formatted date string or null if date is missing
 */
const formatDateForAPI = (date) => {
  if (!date) return null;
  return moment(date).format('YYYY-MM-DD');
};

/**
 * Parses a date range from xLabel (API response format)
 * Handles both single dates and date ranges
 * @param {string} dateRangeString - Date label from API (e.g., "2025-10-08" or "2025-10-08 - 2025-10-15")
 * @param {Date} fallbackStartDate - Fallback start date if parsing fails
 * @param {Date} fallbackEndDate - Fallback end date if parsing fails
 * @returns {Object} Object with startDate and endDate as Date objects
 */
const parseDateRangeFromLabel = (dateRangeString, fallbackStartDate = null, fallbackEndDate = null) => {
  if (!dateRangeString) {
    return {
      startDate: fallbackStartDate,
      endDate: fallbackEndDate || fallbackStartDate
    };
  }

  // Check if it's a date range (contains " - ")
  if (dateRangeString.includes(" - ")) {
    const [startDateStr, endDateStr] = dateRangeString.split(" - ");
    try {
      // Parse dates - they should be in YYYY-MM-DD format from API
      const parsedStartDate = moment(startDateStr.trim()).toDate();
      const parsedEndDate = moment(endDateStr.trim()).toDate();
      return {
        startDate: parsedStartDate,
        endDate: parsedEndDate
      };
    } catch (error) {
      console.warn("Error parsing date range:", error);
      return {
        startDate: fallbackStartDate,
        endDate: fallbackEndDate || fallbackStartDate
      };
    }
  }

  // Single date - use same date for start and end
  try {
    const parsedDate = moment(dateRangeString.trim()).toDate();
    return {
      startDate: parsedDate,
      endDate: parsedDate
    };
  } catch (error) {
    console.warn("Error parsing single date:", error);
    return {
      startDate: fallbackStartDate,
      endDate: fallbackEndDate || fallbackStartDate
    };
  }
};

const filterTableColumnsConfigForSeverity = (tableColumnsConfig, severity = '') => {
  const mapping = {
    "1": "warning",
    "2": "warning",
    "3": "high severity",
    "4": "high severity",
    "5": "critical",
    "severityL1": "warning",
    "severityL2": "warning",
    "severityL3": "high severity",
    "severityL4": "high severity",
    "severityL5": "critical",
  }

  const filteredTableColumnsConfig = {};
  for (const key in tableColumnsConfig) {
    if (Object.keys(mapping).includes(String(key)) && severity.toLowerCase() != mapping[key]?.toLowerCase()) {
      continue;
    };

    filteredTableColumnsConfig[key] = tableColumnsConfig[key];
  }
  return filteredTableColumnsConfig;
  //return tableColumnsConfig;

}

export {
  calculateGranularity,
  formatDateRange,
  formatDateLabel,
  formatDateForAPI,
  parseDateRangeFromLabel,
  filterTableColumnsConfigForSeverity
}
